/**
 * DeskScannerConfigModal (Jul-2026, User-Wunsch) — fester Tischscanner hinterlegen.
 *
 * Speichert die Konfiguration eines festen Beleg-/Dokumentenscanners pro Händler
 * (org-weit am Hauptsitz): Name, Typ (Netzwerk-Scanner eSCL/AirScan, USB via
 * QZ-Tray oder Scan-in-Ordner) sowie Netzwerk-URL/Ordnerpfad.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Save, Printer } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const TYPES = [
    { v: "network", label: "Netzwerk-Scanner (eSCL / AirScan)" },
    { v: "qz_usb", label: "USB-Scanner über QZ-Tray" },
    { v: "folder", label: "Scan-in-Ordner (Netzwerkordner)" },
];

export default function DeskScannerConfigModal({ open, onClose, onSaved }) {
    const [cfg, setCfg] = useState({ enabled: true, name: "", type: "network", network_url: "", folder_path: "", notes: "" });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/wms/desk-scanner")
            .then((r) => { if (r.data.config) setCfg((c) => ({ ...c, ...r.data.config })); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [open]);

    if (!open) return null;

    const save = async () => {
        if (!cfg.name.trim()) { toast.error("Bitte einen Namen für den Scanner angeben"); return; }
        setSaving(true);
        try {
            const r = await api.put("/wms/desk-scanner", cfg);
            toast.success("Tischscanner gespeichert");
            onSaved?.(r.data.config);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSaving(false); }
    };

    const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

    return (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto" data-testid="desk-scanner-modal">
            <div className="bg-white rounded-sm w-full max-w-md shadow-xl my-auto max-h-[96vh] overflow-y-auto">
                <div className="flex items-center justify-between px-4 py-3 bg-[#111827] text-white sticky top-0">
                    <span className="text-sm font-bold uppercase tracking-wider flex items-center gap-2"><Printer className="w-4 h-4 text-[#FFCC00]" /> Tischscanner hinterlegen</span>
                    <button type="button" onClick={onClose} data-testid="desk-scanner-close" aria-label="Schließen" className="p-1 rounded-sm hover:bg-white/10"><X className="w-5 h-5" /></button>
                </div>

                {loading ? (
                    <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                ) : (
                    <div className="p-4 space-y-3">
                        <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
                            <input type="checkbox" checked={cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} className="w-4 h-4" data-testid="desk-scanner-enabled" />
                            Tischscanner aktiv
                        </label>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Bezeichnung</label>
                            <Input value={cfg.name} onChange={(e) => set("name", e.target.value)} placeholder="z. B. Theke Brother ADS-1700W" className="rounded-sm mt-1" data-testid="desk-scanner-name" />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Typ</label>
                            <select value={cfg.type} onChange={(e) => set("type", e.target.value)} className="mt-1 w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white" data-testid="desk-scanner-type">
                                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                            </select>
                        </div>
                        {cfg.type === "network" && (
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Netzwerk-URL (eSCL)</label>
                                <Input value={cfg.network_url} onChange={(e) => set("network_url", e.target.value)} placeholder="http://192.168.1.50/eSCL" className="rounded-sm mt-1" data-testid="desk-scanner-url" />
                            </div>
                        )}
                        {cfg.type === "folder" && (
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Netzwerkordner</label>
                                <Input value={cfg.folder_path} onChange={(e) => set("folder_path", e.target.value)} placeholder="\\\\NAS\\Scans\\Lieferscheine" className="rounded-sm mt-1" data-testid="desk-scanner-folder" />
                            </div>
                        )}
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Notiz (optional)</label>
                            <Input value={cfg.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Standort / Hinweise" className="rounded-sm mt-1" data-testid="desk-scanner-notes" />
                        </div>
                        <p className="text-[11px] text-gray-500">
                            Der hinterlegte Scanner wird auf der Scan-Seite angezeigt. Netzwerk-/USB-Aufnahme erfolgt über den jeweiligen Treiber bzw. QZ-Tray; alternativ Beleg per Kamera oder Datei-Upload erfassen.
                        </p>
                        <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-[11px] text-blue-900 leading-relaxed space-y-2" data-testid="desk-scanner-setup-guide">
                            <div className="font-bold uppercase tracking-wider text-[10px]">So binden Sie den Scanner am Arbeitsplatz ein</div>
                            {cfg.type === "network" && (
                                <>
                                    <p><b>1.</b> Am Multifunktionsgerät „Scan an PC" / „Scan in Ordner" oder „Scan an E-Mail" einrichten (Geräte-Menü bzw. Hersteller-Tool).</p>
                                    <p><b>2.</b> Am Arbeitsplatz die Hersteller-Scan-Software (oder Windows „Scannen"-App) installieren — sie legt jeden Scan als Datei ab oder kopiert ihn in die Zwischenablage.</p>
                                    <p><b>3.</b> In der Scan-Erfassung dann einfach <b>Strg+V</b> drücken oder die Datei per <b>Drag&nbsp;&amp;&nbsp;Drop</b> hineinziehen — ohne Dateidialog. <span className="text-blue-700">Hinweis: Ein direkter eSCL-Zugriff aus dem Browser ist aus Sicherheitsgründen (HTTPS↔LAN) nicht möglich.</span></p>
                                </>
                            )}
                            {cfg.type === "qz_usb" && (
                                <>
                                    <p><b>1.</b> Für USB-Dokumentenscanner empfehlen wir das kostenlose Tool <a href="https://www.naps2.com" target="_blank" rel="noreferrer" className="font-bold underline">NAPS2</a> am jeweiligen Arbeitsplatz: einmal installieren, Scanner-Profil anlegen.</p>
                                    <p><b>2.</b> In NAPS2 „Scan → In Zwischenablage kopieren" (oder Auto-Speichern in einen Ordner) einstellen — ein Tastendruck genügt.</p>
                                    <p><b>3.</b> In der Scan-Erfassung <b>Strg+V</b> drücken bzw. die Datei per Drag&nbsp;&amp;&nbsp;Drop ablegen. <span className="text-blue-700">QZ-Tray selbst unterstützt nur Drucker/Etiketten, kein Dokument-Scannen.</span></p>
                                </>
                            )}
                            {cfg.type === "folder" && (
                                <>
                                    <p><b>1.</b> Am Scanner „Scan in Netzwerkordner" auf den oben angegebenen Pfad einrichten.</p>
                                    <p><b>2.</b> Den Ordner am Arbeitsplatz als Laufwerk/Verknüpfung einbinden (Explorer → Netzlaufwerk verbinden).</p>
                                    <p><b>3.</b> Frisch gescannte Datei per <b>Drag&nbsp;&amp;&nbsp;Drop</b> aus dem Ordner in die Scan-Erfassung ziehen — oder kopieren und mit <b>Strg+V</b> einfügen.</p>
                                </>
                            )}
                            <p className="text-blue-700">Tipp: Ohne Zusatzsoftware funktioniert immer der <b>Handy-QR-Scan</b> (QR auf der Werkstatt-Seite) sowie Kamera/Datei-Upload.</p>
                        </div>
                        <div className="flex gap-2 pt-1">
                            <Button onClick={save} disabled={saving} className="bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider text-xs font-bold flex-1" data-testid="desk-scanner-save">
                                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Speichern
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
